import {
  Button,
  InputNumber,
  Segmented,
  Select,
  Tabs,
  Typography,
  message,
} from "antd";
import { CheckCircleFilled, WarningFilled } from "@ant-design/icons";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import {
  applySavedBusinessPricing,
  selectCurrentBusiness,
} from "../../../businessesSlice";
import {
  loadPartnerWithPricing,
  peekPartnerWithPricing,
} from "./pricingPrefetch";
import {
  IOnlinePurchPriceProfile,
  IPartner,
  IPurchPriceProfile,
  OnlinePartnerBucketGroups,
} from "../../../../../interfaces/partner.interface";
import { BusinessMerchantServiceChargeFee } from "../../../../../interfaces/businessFull.interface";
import PartnerSelector from "../../../../partner-deals/components/partner-selector/PartnerSelector";
import {
  updateBusinessPricingServer,
  type PartnerOnlineMissingLocalMethodGroup,
  type UpdateBusinessPricingPayload,
} from "../../../businessesApi";
import {
  isMerchantSubmittedToAdyen,
  isNoEdit,
  isPartnerRemapLocked,
  PARTNER_REMAP_HINT,
  storedPartnerId,
} from "../../../utils/isNoEdit";
import { resolveCountryAlpha2 } from "../../../utils/resolveCountryAlpha2";
import {
  ONLINE_BUCKET_KEYS,
  ONLINE_BUCKET_LABELS,
  getOnlineBucketKeysForTemplate,
  onlinePricingTemplateForCountry,
  type OnlineBucketKey,
} from "../../../../partners/constants/onlineBuckets";
import {
  BusinessOnlinePricing,
  OnlineGroupKey,
  OnlinePricingStructure,
} from "../../../../../interfaces/onlinePricing.interface";
import {
  buildSixteenBucketOnlinePricing,
  CANNOT_GROUP_PRICING_NOTE,
  coverageHasBuckets,
  isSixteenBucketMerchant,
  shouldLoadOnlinePricingPreview,
  onlineBucketFieldLabel,
  onlineCardSplitHeading,
  onlineGroupValueType,
  orderOnlineBuckets,
  resolveEditorOnlinePricingStructure,
  resolveOnlineGroupCoverage,
  sixteenBucketGroups,
  type OnlineGroupDraft,
  type OnlinePricingGroup,
} from "../../../utils/sixteenBucketOnlinePricing";
import { hasUneditableOnlinePricing } from "../../../utils/uneditableOnlinePricing";
import {
  buildOnlineMerchantServiceChargeFees,
  bucketsMissingARate,
  carriedBucketFields,
  formOnlineBucketRates,
  groupRatesFromBuckets,
  onlineBucketValueType,
  onlineGroupBucketSpread,
  resolveOnlineBucketRates,
  type OnlineBucketRates,
  type OnlineGroupBucketSpread,
  type OnlineRateDraft,
} from "../../../utils/onlineBucketRates";
import {
  getOnlinePricingSyncErrorMessage,
  isAdyenOnlinePricingSyncError,
  isOnlinePricingPayloadError,
  pricingErrorBody,
  resolveInvalidOnlinePricingBuckets,
} from "../../../utils/onlinePricingSyncErrors";
import { useOnlinePricingPreview } from "./useOnlinePricingPreview";
import {
  Band,
  bandRefuses,
  isComparable,
  NO_PARTNER_RANGE_HINT,
  NOT_COMPARABLE_HINT,
  onlineBandFor,
  sixteenBucketOnlineBandFor,
  UNIT_MISMATCH_HINT,
} from "./partnerBands";
import {
  buildInPersonPricingSave,
  buildOnlinePricingSave,
  buildRangeGroups,
  DIRECT_IN_PERSON_CARD_BUCKETS,
  needsInPersonPricing,
  unpricedInPersonCardFees,
  displayFeeLabel,
  feeStatus,
  fillUnpricedOnlinePricing,
  filterOnlinePricingToKeys,
  fmt,
  FeeStatus,
  hasCompleteOnlinePricing,
  hasConfiguredOnlinePricing,
  hasInPersonGroupCoverage,
  hasPricedInPersonBucketGroups,
  inPersonFeeOrder,
  inPersonHasOutOfRange,
  isSixteenBucketOnlineProfile,
  isPricedOnlineValue,
  mappingOnlineGroupRates,
  merchantFeesFromInPersonProfile,
  onlineHasOutOfRange,
  onlinePricingForProfile,
  pricingSaveErrorSection,
  selectOnlinePricingProfile,
  selectPricingProfile,
  seedOnline,
  unmappedFees,
  ValueType,
  type PricingSaveMapping,
  type PricingSaveSection,
} from "./pricingNewTab.helpers";
import {
  reconcileInPersonFeesForSave,
  withInPersonAmexFee,
} from "./merchantServiceFees.helpers";
import { selectHideInPersonAmex } from "../../../../feature-flags/featureFlagsSlice";
import { BucketName } from "../../../../../interfaces/businessFull.interface";

const ACCENT = "#6C4CF1";

const UNPRICED_IN_PERSON_REASON =
  "This merchant has no in-person fees stored. Enter a rate for every bucket to save.";

// One parent grid per group; header and rows are subgrids of it. Status is
// max-content so labels keep the leftover width and wrap at word boundaries.
const PRICING_BAND_COLUMNS = "minmax(0, 1fr) max-content 220px";
const bucketLabelStyle: CSSProperties = {
  minWidth: 0,
  overflowWrap: "normal",
  wordBreak: "normal",
};
// Overlay rows carry a compact range pill AND a status pill; packing both in
// one cell lets a wide € band (iDEAL) shove Markup off the % rows' column.
const PRICING_OVERLAY_COLUMNS = "minmax(0, 1fr) max-content max-content 220px";

function pricingBandParentStyle(
  extra?: CSSProperties,
  columns: string = PRICING_BAND_COLUMNS
): CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: columns,
    columnGap: 12,
    ...extra,
  };
}

function pricingBandSubgridStyle(extra?: CSSProperties): CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: "subgrid",
    gridColumn: "1 / -1",
    alignItems: "center",
    ...extra,
  };
}

function pricingBandRowStyle(extra?: CSSProperties): CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: PRICING_BAND_COLUMNS,
    alignItems: "center",
    columnGap: 12,
    ...extra,
  };
}

// Local-payment-method bucket (iDeal vs bancontact vs neither) varies by the
// business's country/template; card buckets + Apple/Google Pay stay Percentage
// across every template, matching the partner wizard's own convention.
const ONLINE_BUCKET_VALUE_TYPE: Record<OnlineBucketKey, ValueType> = {
  iDeal: "Fixed",
  bancontact: "Fixed",
  amex: "Percentage",
  corporateCreditCards: "Percentage",
  nonCorporateCreditCards: "Percentage",
  appleGooglePay: "Percentage",
};

function onlineBucketsForCountry(country?: string): {
  key: keyof OnlinePartnerBucketGroups;
  label: string;
  valueType: ValueType;
}[] {
  const template = onlinePricingTemplateForCountry(
    resolveCountryAlpha2(country)
  );
  return getOnlineBucketKeysForTemplate(template).map((key) => ({
    key,
    label: ONLINE_BUCKET_LABELS[key],
    valueType: ONLINE_BUCKET_VALUE_TYPE[key],
  }));
}

type InPersonSeedSource =
  | {
      partnerId?: unknown;
      merchantServiceChargeFees?: BusinessMerchantServiceChargeFee[];
    }
  | null
  | undefined;

function feesFromBusiness(
  business: InPersonSeedSource,
  hideInPersonAmex: boolean
): BusinessMerchantServiceChargeFee[] {
  if (needsInPersonPricing(business)) {
    // Amex gets its usual default rate (or stays hidden); the card buckets are
    // left for the operator to price.
    return withInPersonAmexFee(unpricedInPersonCardFees(), {
      hideInPersonAmex,
    });
  }
  const seeded = (business?.merchantServiceChargeFees ?? []).map((f) => ({
    ...f,
  }));
  return hideInPersonAmex
    ? seeded.filter((f) => f.name !== BucketName.InPersonAmex)
    : seeded;
}

/** The rows seeded with no rate, which render empty until the operator prices
 *  them. */
function unpricedFeeNames(business: InPersonSeedSource): ReadonlySet<string> {
  return new Set(
    needsInPersonPricing(business) ? DIRECT_IN_PERSON_CARD_BUCKETS : []
  );
}

const LOCAL_METHOD_LABEL: Record<PartnerOnlineMissingLocalMethodGroup, string> =
  {
    bancontact: "Bancontact",
    iDeal: "iDeal",
  };

// Same test as the backend's refusal: a zero rate is "not priced yet".
const rangePricesGroup = (
  profile: IOnlinePurchPriceProfile,
  group: PartnerOnlineMissingLocalMethodGroup
): boolean => {
  const value = profile.bucketGroups?.[group]?.value;
  return typeof value === "number" && value > 0;
};

const localMethodCopy = (group?: PartnerOnlineMissingLocalMethodGroup) =>
  group
    ? `${LOCAL_METHOD_LABEL[group]}, which this merchant's country requires`
    : "the merchant's local payment method";

function mappingBlockedCopy(params: {
  partnerName?: string;
  // undefined while the partner's ranges are unknown: name the method, not the fix.
  onlineProfiles: IOnlinePurchPriceProfile[] | undefined;
  group?: PartnerOnlineMissingLocalMethodGroup;
}): string {
  const { group, onlineProfiles } = params;
  const method = localMethodCopy(group);
  const whose = params.partnerName
    ? `${params.partnerName}'s`
    : "This partner's";
  if (onlineProfiles === undefined) {
    return `${whose} online pricing does not price ${method}.`;
  }
  // The refusal judges the selected range only, so another range may still
  // price the method. Without the group, any range is the best guess.
  const anotherRangePricesIt = group
    ? onlineProfiles.some((profile) => rangePricesGroup(profile, group))
    : onlineProfiles.length > 0;
  if (!anotherRangePricesIt) {
    return `${params.partnerName ?? "This partner"} has no online price range that prices ${method}. Create one on the partner, then map.`;
  }
  return `${whose} selected online range does not price ${method}. Choose a range that prices it.`;
}

function onlineUnlinkedCopy(params: {
  partnerName?: string;
  group?: PartnerOnlineMissingLocalMethodGroup;
}): string {
  return `${params.partnerName ?? "This partner"} has no online price range that prices ${localMethodCopy(params.group)}. The merchant is mapped in person only.`;
}

type PricingSection = PricingSaveSection;
type SaveSection = Exclude<PricingSection, "mapping">;

// Combined Pricing tab (PC-438): in-person fees + online pricing + partner
// mapping, saved via the dedicated PUT /businesses/{id}/pricing endpoint. Each
// tab saves on its own button and either one writes the mapping (PC-1316), so
// a save leaves the modal open for the other section.
// No `visible` prop: the closed Modal does not re-render this pane, so it would
// read true here for as long as the pane is mounted.
export default function PricingNewTab({
  openCount,
}: {
  /** Bumped by the modal on every open; see the re-seed effect below. */
  openCount?: number;
}) {
  const currentBusiness = useAppSelector(selectCurrentBusiness);
  const hideInPersonAmex = useAppSelector(selectHideInPersonAmex);
  const dispatch = useAppDispatch();

  // Country template plus leftover four-bucket keys that already have a value
  // (a BE merchant still on iDeal). 16-bucket groups are not leftovers; the save
  // sends this exact key set, so an omitted stored fee would be dropped.
  const ONLINE_BUCKETS = useMemo(() => {
    const template = onlineBucketsForCountry(currentBusiness?.address?.country);
    const templateKeys = new Set(template.map((b) => b.key));
    const fourBucketKeys = new Set<string>(ONLINE_BUCKET_KEYS);
    const legacy = Object.keys(currentBusiness?.onlinePricing ?? {})
      .filter((key): key is OnlineBucketKey => fourBucketKeys.has(key))
      .filter(
        (key) =>
          !templateKeys.has(key) &&
          typeof currentBusiness?.onlinePricing?.[key]?.value === "number"
      )
      .map((key) => ({
        key,
        label: ONLINE_BUCKET_LABELS[key],
        valueType: ONLINE_BUCKET_VALUE_TYPE[key],
      }));
    return [...template, ...legacy];
  }, [currentBusiness?.address?.country, currentBusiness?.onlinePricing]);

  const submittedToAdyen = isMerchantSubmittedToAdyen(currentBusiness);
  const onlineDisabled = isNoEdit(currentBusiness?.noEdit, "onlinePricing");
  const partnerRemapLocked = isPartnerRemapLocked(currentBusiness);

  const initialPartnerId = useMemo(
    () =>
      (currentBusiness?.partnerId as unknown as { _id?: string })?._id ??
      (currentBusiness?.partnerId as string | undefined),
    [currentBusiness?.partnerId]
  );

  const [fees, setFees] = useState(() =>
    feesFromBusiness(currentBusiness, hideInPersonAmex)
  );
  const [unpricedFees, setUnpricedFees] = useState(() =>
    unpricedFeeNames(currentBusiness)
  );
  const [onlinePricing, setOnlinePricing] = useState(() =>
    seedOnline(currentBusiness?.onlinePricing)
  );
  // A 16-bucket merchant's edits are held as an overlay on the stored rates
  // rather than a seeded copy of them: a bucket the operator has not touched must
  // read as the rate the merchant actually carries, and seeding would have to
  // invent one — the accidental zero the backend persists silently.
  const [onlineBucketDraft, setOnlineBucketDraft] = useState<
    Partial<Record<string, OnlineRateDraft>>
  >({});
  // The backend's own refusal of the last save. Kept on the pane rather than in
  // a toast: it names the bucket at fault, and nothing was persisted, so the
  // operator is being asked to fix the form and try again.
  const [onlineSaveError, setOnlineSaveError] = useState<{
    message: string;
    buckets: string[];
  } | null>(null);
  // The same, for a refusal that belongs to the other section or the mapping.
  const [inPersonSaveError, setInPersonSaveError] = useState<string | null>(
    null
  );
  const [mappingSaveError, setMappingSaveError] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState(initialPartnerId);
  const [profileId, setProfileId] = useState(
    () => currentBusiness?.partnerPurchPriceProfileId || undefined
  );
  const [
    partnerOnlinePurchPriceProfileId,
    setPartnerOnlinePurchPriceProfileId,
  ] = useState(
    () => currentBusiness?.partnerOnlinePurchPriceProfileId || undefined
  );
  const [selectedPartner, setSelectedPartner] = useState<IPartner>();
  const [profiles, setProfiles] = useState<IPurchPriceProfile[]>();
  const [onlineProfiles, setOnlineProfiles] =
    useState<IOnlinePurchPriceProfile[]>();
  const [selectedProfile, setSelectedProfile] = useState<IPurchPriceProfile>();
  const [selectedOnlineProfile, setSelectedOnlineProfile] =
    useState<IOnlinePurchPriceProfile>();
  const [partnerPricingLoading, setPartnerPricingLoading] = useState(false);
  // The section whose save is in flight; the other button waits for it.
  const [saving, setSaving] = useState<SaveSection>();
  const [activeTab, setActiveTab] = useState("inperson");
  const pendingOnlineMappingRef = useRef<string | undefined>(undefined);
  const pendingInPersonMappingRef = useRef<string | undefined>(undefined);
  const fourBucketMappingSeededRef = useRef<string | undefined>(undefined);
  // The four-bucket rates the last range seed put in, so the next range switch
  // can tell them from the merchant's own — see fillUnpricedOnlinePricing.
  const onlineRangeSeedRef = useRef<OnlinePartnerBucketGroups>({});
  // Which sections hold an unsaved edit, which is what decides if a fresh
  // business snapshot may replace them — see the re-seed effect below. The two
  // sections save on their own buttons (PC-1316), so saving one must not wipe
  // a draft the other still holds. The mapping is written by either save.
  const dirtyRef = useRef<Record<PricingSection, boolean>>({
    inPerson: false,
    online: false,
    mapping: false,
  });
  const seededBusinessIdRef = useRef<string | undefined>(undefined);
  const markDirty = (...sections: PricingSection[]) => {
    sections.forEach((section) => {
      dirtyRef.current[section] = true;
    });
  };
  const isDirty = () => Object.values(dirtyRef.current).some(Boolean);

  // (Re)initialize local edit state from the persisted business, discarding the
  // in-memory draft of each section named (all of them by default). Reads from
  // the current Redux snapshot at call time.
  const seedFromBusiness = useCallback(
    (sections: PricingSection[] = ["inPerson", "online", "mapping"]) => {
      if (sections.includes("inPerson")) {
        setFees(feesFromBusiness(currentBusiness, hideInPersonAmex));
        setUnpricedFees(unpricedFeeNames(currentBusiness));
        // The online save leaves the in-person range as stored, so a pick is
        // the in-person draft and survives an online save with it.
        setProfileId(currentBusiness?.partnerPurchPriceProfileId || undefined);
        pendingInPersonMappingRef.current = undefined;
        dirtyRef.current.inPerson = false;
        setInPersonSaveError(null);
      }
      if (sections.includes("online")) {
        setOnlinePricing(seedOnline(currentBusiness?.onlinePricing));
        pendingOnlineMappingRef.current = undefined;
        fourBucketMappingSeededRef.current = undefined;
        onlineRangeSeedRef.current = {};
        // The in-person save leaves the online range as stored, so a pick is
        // the online draft and survives an in-person save with it.
        setPartnerOnlinePurchPriceProfileId(
          currentBusiness?.partnerOnlinePurchPriceProfileId || undefined
        );
        dirtyRef.current.online = false;
        setOnlineBucketDraft({});
        setOnlineSaveError(null);
      }
      if (sections.includes("mapping")) {
        pendingOnlineMappingRef.current = undefined;
        pendingInPersonMappingRef.current = undefined;
        dirtyRef.current.mapping = false;
        setMappingSaveError(null);
        setPartnerId(initialPartnerId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentBusiness, initialPartnerId, hideInPersonAmex]
  );

  // Follow the persisted business: the Merchant Service Fees tab saves the same
  // in-person fees, and each save here refetches it, so this pane must show
  // what landed. A section holding an unsaved draft keeps it; a different
  // business re-seeds everything.
  useEffect(() => {
    const businessChanged =
      seededBusinessIdRef.current !== currentBusiness?._id;
    seededBusinessIdRef.current = currentBusiness?._id;
    if (businessChanged) {
      seedFromBusiness();
      return;
    }
    const clean = (Object.keys(dirtyRef.current) as PricingSection[]).filter(
      (section) => !dirtyRef.current[section]
    );
    if (clean.length) seedFromBusiness(clean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusiness]);

  // Re-seed from the persisted DB values every time the modal opens (PC-697).
  // The modal has no destroyOnClose, so this component stays mounted across
  // close/reopen and would otherwise keep an unsaved (possibly invalid) draft.
  // Because invalid values are gated from saving, a close-without-save edit must
  // never survive the next open — reopening must reflect the server value.
  // Keyed on the open count, since the pane cannot see the Modal close.
  const prevOpenCountRef = useRef(openCount);
  useEffect(() => {
    if (openCount === prevOpenCountRef.current) return;
    prevOpenCountRef.current = openCount;
    seedFromBusiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  useEffect(() => {
    let cancelled = false;
    const applyPartner = (partner: IPartner | undefined) => {
      if (cancelled) return;
      if (!partner) {
        setSelectedPartner(undefined);
        setProfiles(undefined);
        setOnlineProfiles(undefined);
        return;
      }
      setSelectedPartner(partner);
      setProfiles(partner.purchPriceProfiles);
      setOnlineProfiles(partner.onlinePurchPriceProfiles);
    };
    if (!partnerId) {
      setSelectedPartner(undefined);
      setProfiles(undefined);
      setOnlineProfiles(undefined);
      setSelectedProfile(undefined);
      setSelectedOnlineProfile(undefined);
      setPartnerPricingLoading(false);
      return;
    }

    setSelectedProfile(undefined);
    setSelectedOnlineProfile(undefined);

    const cached = peekPartnerWithPricing(partnerId);
    if (cached.hit) {
      applyPartner(cached.value);
      setPartnerPricingLoading(false);
      return;
    }

    setSelectedPartner(undefined);
    setProfiles(undefined);
    setOnlineProfiles(undefined);
    setPartnerPricingLoading(true);
    loadPartnerWithPricing(partnerId)
      .then(applyPartner)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPartnerPricingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  const bandFor = (key: keyof OnlinePartnerBucketGroups) => {
    if (partnerOnlineUnlinked) return undefined;
    if (partnerOnlinePurchPriceProfileId && !selectedOnlineProfile)
      return undefined;
    return onlineBandFor(key, {
      profile: selectedOnlineProfile,
      partnerDefault: selectedPartner?.defaultOnlinePricing,
    });
  };
  const configuredOnlineBand = (
    band?: Band
  ): { min: number; max: number; valueType?: string } | undefined => {
    if (
      !band ||
      typeof band.min !== "number" ||
      typeof band.max !== "number" ||
      (band.min === 0 && band.max === 0)
    ) {
      return undefined;
    }
    return { min: band.min, max: band.max, valueType: band.valueType };
  };

  // Pane: labelled sixteen-bucket follows the preview. Before Adyen, an
  // unlabelled or four-bucket merchant follows a picked 16-bucket range; only
  // the four-bucket one holds that pane while the range list loads.
  const isUneditableOnline = hasUneditableOnlinePricing(currentBusiness);
  const mappedUnpricedOnline =
    !!partnerId &&
    !hasConfiguredOnlinePricing(
      currentBusiness?.onlinePricing,
      ONLINE_BUCKETS.map((bucket) => bucket.key)
    );
  const preferSixteenOnlineProfile =
    !submittedToAdyen &&
    (currentBusiness?.onlinePricingStructure ===
      OnlinePricingStructure.SixteenBucket ||
      mappedUnpricedOnline) &&
    (onlineProfiles === undefined ||
      !!onlineProfiles.some(isSixteenBucketOnlineProfile));
  const onlinePreview = useOnlinePricingPreview({
    businessId: currentBusiness?._id,
    partnerId,
    partnerOnlinePurchPriceProfileId,
    enabled: shouldLoadOnlinePricingPreview({
      business: currentBusiness,
      partnerId,
    }),
  });
  const previewStructure =
    onlinePreview.preview?.status === "ok"
      ? onlinePreview.preview.pricing?.onlinePricingStructure
      : undefined;
  const selectedProfileIsSixteenBucket =
    !submittedToAdyen &&
    isSixteenBucketOnlineProfile(
      onlineProfiles?.find(
        (profile) => profile._id === partnerOnlinePurchPriceProfileId
      )
    );
  const profilePickPending =
    !submittedToAdyen && !!partnerId && onlineProfiles === undefined;
  const isSixteenBucket =
    resolveEditorOnlinePricingStructure({
      business: currentBusiness,
      previewStructure:
        // Only the no-id leftover default; an explicit four-bucket pick keeps that renderer.
        preferSixteenOnlineProfile &&
        !partnerOnlinePurchPriceProfileId &&
        previewStructure === OnlinePricingStructure.FourBucket
          ? undefined
          : previewStructure,
      mappedPartner: !!partnerId,
      followPartnerPreview: false,
      selectedProfileIsSixteenBucket,
      profilePickPending,
    }) === OnlinePricingStructure.SixteenBucket;
  const partnerOnlineMappingBlocked =
    !!partnerId && onlinePreview.preview?.mappingBlocked === true;
  // Mapped in person only: the save leaves the online range and rates alone, so
  // the pane shows the merchant's own rates with no partner seed or band on them.
  const partnerOnlineUnlinked =
    !!partnerId &&
    !partnerOnlineMappingBlocked &&
    onlinePreview.preview?.onlineUnlinked === true;
  const onlineLocked = onlineDisabled || partnerOnlineUnlinked;

  useEffect(() => {
    const partnerLoaded = !!partnerId && selectedPartner?._id === partnerId;
    if (!partnerLoaded || !selectedPartner) return;
    if (profiles === undefined || onlineProfiles === undefined) return;

    const mappingInProgress = pendingInPersonMappingRef.current === partnerId;
    const profile = selectPricingProfile(
      profiles,
      mappingInProgress ? undefined : profileId
    );
    const onlineProfile = selectOnlinePricingProfile(
      onlineProfiles,
      mappingInProgress ? undefined : partnerOnlinePurchPriceProfileId,
      mappingInProgress ? false : isSixteenBucket || preferSixteenOnlineProfile
    );
    setSelectedProfile(profile);
    setSelectedOnlineProfile(onlineProfile);
    // Unlinked online maps no range, so none is adopted for the select to show.
    const adoptOnlineProfile = !!onlineProfile && !partnerOnlineUnlinked;
    if (mappingInProgress) {
      if (profile) setProfileId(profile._id);
      if (adoptOnlineProfile) {
        setPartnerOnlinePurchPriceProfileId(onlineProfile._id);
      }
      const expanded = merchantFeesFromInPersonProfile(profile);
      if (expanded.length) {
        setFees(
          hideInPersonAmex
            ? expanded.filter((f) => f.name !== BucketName.InPersonAmex)
            : expanded
        );
        // The range priced every row it seeded.
        setUnpricedFees(new Set());
      }
      pendingInPersonMappingRef.current = undefined;
    } else {
      if (!profileId && profile) setProfileId(profile._id);
      if (!partnerOnlinePurchPriceProfileId && adoptOnlineProfile) {
        setPartnerOnlinePurchPriceProfileId(onlineProfile._id);
      }
    }
    const fourBucketKeys = ONLINE_BUCKETS.map((bucket) => bucket.key);
    const fillUnpricedFourBucket =
      !isSixteenBucket &&
      !partnerOnlineUnlinked &&
      (pendingOnlineMappingRef.current === partnerId || !isDirty()) &&
      !hasConfiguredOnlinePricing(
        currentBusiness?.onlinePricing,
        fourBucketKeys
      );
    if (
      fillUnpricedFourBucket &&
      onlineProfiles !== undefined &&
      !onlinePreview.loading
    ) {
      const filled = onlinePricingForProfile(
        onlineProfile,
        selectedPartner.defaultOnlinePricing
      );
      const seedKey = `${partnerId}:${onlineProfile?._id ?? ""}`;
      if (
        hasConfiguredOnlinePricing(filled, fourBucketKeys) &&
        (pendingOnlineMappingRef.current === partnerId ||
          fourBucketMappingSeededRef.current !== seedKey)
      ) {
        fourBucketMappingSeededRef.current = seedKey;
        onlineRangeSeedRef.current = filled;
        setOnlinePricing(filled);
      }
      if (pendingOnlineMappingRef.current === partnerId) {
        pendingOnlineMappingRef.current = undefined;
      }
    } else if (
      pendingOnlineMappingRef.current === partnerId &&
      onlineProfiles !== undefined &&
      !onlinePreview.loading
    ) {
      pendingOnlineMappingRef.current = undefined;
    }
  }, [
    partnerId,
    profiles,
    onlineProfiles,
    profileId,
    partnerOnlinePurchPriceProfileId,
    selectedPartner,
    isSixteenBucket,
    preferSixteenOnlineProfile,
    hideInPersonAmex,
    onlinePreview.loading,
    partnerOnlineUnlinked,
    currentBusiness?.onlinePricing,
  ]);

  // An unlinked save sends no online pricing, so the locked rows go back to what
  // is stored: an edit made before the partner was picked would never land.
  useEffect(() => {
    if (!partnerOnlineUnlinked) return;
    setOnlinePricing(seedOnline(currentBusiness?.onlinePricing));
    setOnlineBucketDraft({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerOnlineUnlinked]);

  const previewOnlinePricing = onlinePreview.preview?.pricing?.onlinePricing;
  // `partner_has_no_online_pricing` is the only status that means the range
  // resolved for this mapping prices none of the merchant's groups (or there is
  // none). Another of the partner's ranges may still price them. A missing
  // local method is not that.
  const partnerHasNoOnlineRange =
    !!partnerId &&
    onlinePreview.preview?.status === "partner_has_no_online_pricing";
  const partnerRangeMissingLocalMethod =
    !!partnerId &&
    onlinePreview.preview?.status === "partner_range_missing_local_method";
  const mappingBlockedReason = mappingBlockedCopy({
    partnerName: selectedPartner?.name,
    onlineProfiles,
    group: onlinePreview.preview?.missingLocalMethodGroup,
  });
  const onlineUnlinkedReason = onlineUnlinkedCopy({
    partnerName: selectedPartner?.name,
    group: onlinePreview.preview?.missingLocalMethodGroup,
  });
  // A priced or submitted 16-bucket merchant stays on 16 buckets under a
  // four-bucket range (see resolveEditorOnlinePricingStructure), but that
  // range's preview serves four buckets and no grouping. Its groups then come
  // from the merchant's own snapshot, as for a partner with no range at all;
  // the bands the four-bucket range shares (AMEX, wallet, local method) still
  // apply through the preview.
  const sixteenUnderFourBucketRange =
    isSixteenBucket &&
    !!partnerId &&
    previewStructure === OnlinePricingStructure.FourBucket;
  const coverageFromMerchant =
    partnerHasNoOnlineRange || sixteenUnderFourBucketRange;
  // Mapped preview omits coverage on these. The unmapped path still returns the
  // merchant snapshot — omit partnerId on useOnlinePricingPreview.
  const merchantCoveragePreview = useOnlinePricingPreview({
    businessId: currentBusiness?._id,
    enabled:
      isSixteenBucket &&
      coverageFromMerchant &&
      !onlinePreview.loading &&
      !onlinePreview.failed,
  });
  const waitingForMerchantCoverage =
    coverageFromMerchant &&
    !onlinePreview.failed &&
    (merchantCoveragePreview.loading ||
      (!merchantCoveragePreview.preview && !merchantCoveragePreview.failed));
  const previewCoverage = coverageFromMerchant
    ? merchantCoveragePreview.preview?.pricing?.onlineBucketGroupCoverage
    : onlinePreview.preview?.pricing?.onlineBucketGroupCoverage;
  const profileCoverage = coverageFromMerchant
    ? undefined
    : selectedOnlineProfile?.onlineBucketGroupCoverage;
  const onlineCoverage = resolveOnlineGroupCoverage(
    previewCoverage,
    profileCoverage
  );
  const useSelectedProfileBands =
    !coverageFromMerchant &&
    coverageHasBuckets(profileCoverage) &&
    onlineCoverage === profileCoverage;
  const onlineGroups = useMemo(
    () =>
      isSixteenBucket
        ? sixteenBucketGroups(currentBusiness, onlineCoverage)
        : [],
    [isSixteenBucket, currentBusiness, onlineCoverage]
  );

  const groupBandFor = (key: OnlineGroupKey): Band | undefined =>
    useSelectedProfileBands
      ? onlineBandFor(key, {
          profile: selectedOnlineProfile,
          partnerDefault: selectedPartner?.defaultOnlinePricing,
        })
      : sixteenBucketOnlineBandFor(key, {
          previewPricing: previewOnlinePricing,
          hasPartner: !!partnerId,
        });

  const mappingGroupRates = useMemo(
    () =>
      partnerOnlineUnlinked
        ? {}
        : mappingOnlineGroupRates({
            profile: selectedOnlineProfile,
            partnerDefault: selectedPartner?.defaultOnlinePricing,
          }),
    [
      partnerOnlineUnlinked,
      selectedOnlineProfile,
      selectedPartner?.defaultOnlinePricing,
    ]
  );

  // The rate a group carries today: the fan-out fallback for a merchant whose
  // rows do not cover every bucket it holds, and the unit its band is read in.
  // `null` means the group has no rate at all, so it prices no bucket.
  const resolveGroupRate = (
    key: OnlineGroupKey,
    band?: Band
  ): OnlineGroupDraft => {
    const stored = currentBusiness?.onlinePricing?.[key];
    const previewGroup = previewOnlinePricing?.[key];
    const mappingGroup = mappingGroupRates[key];
    const storedValue = stored?.value;
    const previewValue = previewGroup?.value;
    const value = isPricedOnlineValue(storedValue)
      ? storedValue
      : partnerId && isPricedOnlineValue(previewValue)
        ? previewValue
        : (mappingGroup?.value ?? null);
    return {
      value,
      // The stored unit wins: reading a stored €1.50 back as 1.5% would reprice
      // the fee on screen. Preview, then the band, fill an unpriced group.
      valueType:
        (stored?.valueType as ValueType | undefined) ??
        (previewGroup?.valueType as ValueType | undefined) ??
        mappingGroup?.valueType ??
        (band?.valueType as ValueType | undefined) ??
        onlineGroupValueType(key) ??
        "Percentage",
    };
  };

  const onlineGroupRows = useMemo(
    () =>
      onlineGroups.map((group) => {
        const band = groupBandFor(group.key);
        return { group, band, rate: resolveGroupRate(group.key, band) };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onlineGroups,
      previewOnlinePricing,
      currentBusiness?.onlinePricing,
      partnerId,
      mappingGroupRates,
      useSelectedProfileBands,
      selectedOnlineProfile,
      selectedPartner?.defaultOnlinePricing,
    ]
  );

  const resolvedGroupDraft = useMemo(() => {
    const draft: Partial<Record<OnlineGroupKey, OnlineGroupDraft>> = {};
    onlineGroupRows.forEach(({ group, rate }) => {
      draft[group.key] = rate;
    });
    return draft;
  }, [onlineGroupRows]);

  // A 16-bucket merchant's rate is stored per BUCKET, and a bucket can be priced
  // away from its group — so a bucket row's rate has to be resolved, never read
  // off the group it happens to sit under.
  const persistedBucketRates = useMemo(
    () =>
      resolveOnlineBucketRates({
        groups: onlineGroups,
        coverage: onlineCoverage,
        storedFees: currentBusiness?.onlineMerchantServiceChargeFees,
        groupRates: resolvedGroupDraft,
      }),
    [
      onlineGroups,
      onlineCoverage,
      currentBusiness?.onlineMerchantServiceChargeFees,
      resolvedGroupDraft,
    ]
  );

  const onlineBucketRates = useMemo(
    () =>
      formOnlineBucketRates({
        persisted: persistedBucketRates,
        editedBuckets: onlineBucketDraft,
      }),
    [persistedBucketRates, onlineBucketDraft]
  );

  // Every 16-bucket merchant is priced per bucket, a partner-mapped one included:
  // its rows are each held to the band of the group they sit under rather than
  // being refused for diverging from it.
  const bucketsEditable = isSixteenBucket && !onlineLocked;

  // Ungrouped buckets in canonical order. Empty coverage falls back to stored fees.
  const onlineBuckets = useMemo(() => {
    const fromCoverage = orderOnlineBuckets(
      onlineGroups.flatMap((group) => group.buckets)
    );
    if (fromCoverage.length) return fromCoverage;
    return orderOnlineBuckets(
      (currentBusiness?.onlineMerchantServiceChargeFees ?? []).map((fee) => ({
        field: fee.name,
        label: onlineBucketFieldLabel(fee.name),
      }))
    );
  }, [onlineGroups, currentBusiness?.onlineMerchantServiceChargeFees]);

  // The merchant is priced on its buckets, so the group snapshot a save carries
  // is derived from them — see groupRatesFromBuckets. A derived value its band
  // refuses is left out: that check is absolute.
  const groupDraftToSave = useMemo(() => {
    const derived = groupRatesFromBuckets(onlineGroups, onlineBucketRates);
    if (!onlineGroups.some((group) => group.buckets.length)) {
      onlineGroups.forEach(({ key }) => {
        const stored = currentBusiness?.onlinePricing?.[key];
        if (typeof stored?.value === "number") {
          derived[key] = {
            value: stored.value,
            valueType: (stored.valueType as ValueType) ?? "Percentage",
          };
        }
      });
    }
    onlineGroupRows.forEach(({ group, band }) => {
      const rate = derived[group.key];
      if (band && rate && bandRefuses(rate, band)) delete derived[group.key];
    });
    return derived;
  }, [
    onlineGroups,
    onlineBucketRates,
    onlineGroupRows,
    currentBusiness?.onlinePricing,
  ]);

  // Every carried bucket needs a rate or the per-bucket payload is incomplete,
  // which is a 422 that persists nothing.
  const bucketsWithoutARate = bucketsEditable
    ? onlineGroups.some((group) => group.buckets.length)
      ? bucketsMissingARate(onlineGroups, onlineBucketRates)
      : onlineBuckets.filter((bucket) => !onlineBucketRates[bucket.field])
    : [];

  // Each carried bucket beside the band of the group it sits under. Out of band
  // blocks the online save even when the rate was already stored — that save is
  // how those fees would first land, and a later tighter band still has to be
  // met. It blocks only the online save (PC-1316).
  const onlineBucketRows = useMemo(
    () =>
      onlineGroupRows.flatMap(({ group, band }) =>
        group.buckets.map((bucket) => {
          const rate = onlineBucketRates[bucket.field];
          const comparable =
            !band?.valueType || !rate
              ? true
              : isComparable(rate.valueType, band.valueType);
          const banded = band && comparable && rate ? band : undefined;
          // A 0 – 0 band is the placeholder `onlineBandFor` synthesises for a
          // group the partner prices nothing for, not a demand that the merchant
          // charge nothing. Judging a row by it would refuse every rate it
          // carries and leave the save blocked with nowhere for the operator to
          // go — so it is dropped here, the way the four-bucket pane drops it.
          const bounds = configuredOnlineBand(banded);
          const status =
            bounds && rate
              ? feeStatus(rate.value, rate.valueType, bounds.min, bounds.max)
              : null;
          // Stale percentage under a euro band has to be re-priced.
          const unitMismatch = !comparable && !!rate && rate.value > 0;
          return {
            ...bucket,
            band,
            rate,
            status,
            comparable,
            unitMismatch,
            // Says the pane has no band to judge this rate by, and nothing more:
            // the preview reports the same status for a partner priced on the
            // legacy four buckets, whose range the backend accepts a save
            // against. So it annotates the row without blocking it.
            missingPartnerRange: partnerHasNoOnlineRange,
            blocking:
              unitMismatch ||
              (!!status &&
                (status.kind === "below" || status.kind === "above")),
          };
        })
      ),
    [onlineGroupRows, onlineBucketRates, partnerHasNoOnlineRange]
  );

  const onlineBucketRowByField = useMemo(
    () =>
      onlineBucketRows.reduce<Record<string, OnlineBucketBandRow>>(
        (map, row) => {
          map[row.field] = row;
          return map;
        },
        {}
      ),
    [onlineBucketRows]
  );
  // Groups the partner prices but we have no floor/ceiling for. The save can
  // still go through (the backend is the final judge), but the operator should
  // know this pane is not checking those rows.
  const groupsWithoutABand = onlineGroupRows
    .filter(({ band }) => !band)
    .map(({ group }) => group.label);

  // The flat in-person list is ordered here rather than in `fees`, which is the
  // save payload and is edited by index.
  const feeRenderOrder = useMemo(() => inPersonFeeOrder(fees), [fees]);

  // Group in-person fees under their partner range group (mock "with partner").
  const rangeGroups = useMemo(
    () => buildRangeGroups(fees, selectedProfile),
    [selectedProfile, fees]
  );

  // The endpoint requires a complete onlinePricing and blindly $sets it, so
  // saving a merchant that has none would zero it — block until it's configured.
  //
  // PC-1010: a merchant priced for the first time has nothing stored, and would
  // never get a first save if that were the whole test. Typing into the form is
  // the other way it becomes configured — and an untouched empty pane still
  // sends nothing, so an unpriced merchant cannot be zeroed by a save of its
  // in-person fees or its partner mapping.
  const onlineTouched = Object.keys(onlineBucketDraft).length > 0;
  // Never configured for a save's purposes: this is what keeps onlinePricing out
  // of the payload, leaving what is stored exactly as it is.
  // Partner mapping defaults on an unpriced merchant are the rates Confirm
  // writes; preview zeros are not a rate and do not count.
  const mappingDefaultsFillUnpriced =
    isSixteenBucket &&
    !!partnerId &&
    !hasConfiguredOnlinePricing(
      currentBusiness?.onlinePricing,
      onlineGroups.map((group) => group.key)
    ) &&
    Object.values(mappingGroupRates).some((rate) =>
      isPricedOnlineValue(rate.value)
    ) &&
    bucketsWithoutARate.length === 0;
  const fourBucketKeys = ONLINE_BUCKETS.map((bucket) => bucket.key);
  const fourBucketHasRates = hasConfiguredOnlinePricing(
    onlinePricing,
    fourBucketKeys
  );
  const fourBucketComplete = hasCompleteOnlinePricing(
    onlinePricing,
    fourBucketKeys
  );
  const onlineConfigured = isUneditableOnline
    ? false
    : isSixteenBucket
      ? onlineTouched ||
        hasConfiguredOnlinePricing(
          currentBusiness?.onlinePricing,
          onlineGroups.map((group) => group.key)
        ) ||
        mappingDefaultsFillUnpriced
      : hasConfiguredOnlinePricing(
            currentBusiness?.onlinePricing,
            fourBucketKeys
          )
        ? fourBucketHasRates
        : fourBucketComplete;

  const inPersonOutOfRange = inPersonHasOutOfRange(rangeGroups);
  const onlineOutOfRange =
    onlineConfigured &&
    !onlineLocked &&
    (isSixteenBucket
      ? onlineBucketRows.some((row) => row.blocking)
      : !!selectedPartner &&
        onlineHasOutOfRange(
          ONLINE_BUCKETS.map(({ key, valueType }) => {
            const band = configuredOnlineBand(bandFor(key));
            return {
              value: onlinePricing[key]?.value ?? 0,
              valueType,
              min: band?.min,
              max: band?.max,
            };
          })
        ));
  const hasPartner = !!partnerId;
  // A preview that never answered leaves the same empty coverage as one that
  // answered with none, and only the second means there is nothing to price. A
  // mapped merchant still has its stored group rates to show; a direct one that
  // has never been priced has nothing left, so the card would read as a verdict.
  const onlineRangesUnavailable =
    onlinePreview.failed && !hasPartner && !onlineBuckets.length;
  // Grouping is a partner range's own shape, so only a mapped merchant reads as
  // groups. A direct one is priced per bucket against no range and reads the way
  // the in-person pane does: a flat grid of Klearly-direct buckets.
  const showOnlineGroups = hasPartner;
  const partnerPricingUnavailable =
    hasPartner &&
    (partnerPricingLoading ||
      !selectedPartner ||
      (!!profileId && !selectedProfile) ||
      (!!partnerOnlinePurchPriceProfileId &&
        !selectedOnlineProfile &&
        !!onlineProfiles?.length) ||
      // Saving before the partner bands land would submit rates this pane never
      // checked, which the backend answers with a 422 rather than a warning.
      (isSixteenBucket &&
        (onlinePreview.loading || waitingForMerchantCoverage)));
  const ungrouped = hasPartner ? unmappedFees(fees, selectedProfile) : [];
  const partnerHasInPersonRange = (profiles ?? []).some(
    (candidate) =>
      hasInPersonGroupCoverage(candidate) ||
      hasPricedInPersonBucketGroups(candidate)
  );
  const inPersonCannotGroup =
    hasPartner &&
    !!selectedProfile &&
    !hasInPersonGroupCoverage(selectedProfile) &&
    !hasPricedInPersonBucketGroups(selectedProfile);
  const inPersonRangeState = ((): PartnerRangeState => {
    if (rangeGroups.length > 0) return "overlaid";
    if (partnerPricingLoading) return "loading";
    if (!partnerHasInPersonRange) return "none";
    return "unknown";
  })();
  // Whether a partner online range actually reached the rows. A mapped partner
  // is not the same thing as a loaded range — the profile arrives after the
  // first render, and most partners carry no online band at all — so the rows
  // themselves decide, the same way the 16-bucket pane names the groups it
  // could not check.
  const onlineRangeLoaded =
    hasPartner &&
    (isSixteenBucket
      ? onlineGroupRows.some(({ band }) => !!band)
      : ONLINE_BUCKETS.some(({ key }) => !!configuredOnlineBand(bandFor(key))));
  // Only the preview's own verdict can put a partner behind a missing range.
  // Absent bands do not carry it: a request that never landed has none, and
  // neither do the four other statuses that answer without pricing — a merchant
  // already submitted to Adyen, a missing BRN, an unknown partner, an
  // unconfigured Klearly cut. So the negative copy is gated on the one status
  // that means it, and everything else falls through to "not known here",
  // which overstates an ok answer carrying no band for these groups but never
  // asserts a fact about the partner that nothing established.
  const onlineRangeState = ((): PartnerRangeState => {
    if (onlineRangeLoaded) return "overlaid";
    // The four-bucket pane reads the partner profile straight off the mapping
    // rather than through the preview, so an empty pane there is the partner.
    if (!isSixteenBucket) return "none";
    if (onlinePreview.loading) return "loading";
    return partnerHasNoOnlineRange ? "none" : "unknown";
  })();
  const onlineFourBucketMeta = !hasPartner
    ? "Klearly direct fees"
    : onlineRangeLoaded
      ? "partner range overlaid"
      : "no partner range loaded";

  const setFeeValue = (index: number, value: number | null) => {
    markDirty("inPerson");
    setFees((prev) =>
      prev.map((f, i) => (i === index ? { ...f, value: value ?? 0 } : f))
    );
    const name = fees[index]?.name;
    // A row seeded unpriced goes back to unpriced when cleared, rather than
    // to a 0 % rate that would save as a real one.
    if (name && (value !== null || needsInPersonPricing(currentBusiness))) {
      setUnpricedFees((prev) => {
        if ((value === null) === prev.has(name)) return prev;
        const next = new Set(prev);
        if (value === null) next.add(name);
        else next.delete(name);
        return next;
      });
    }
  };
  const feeDisplayValue = (fee: BusinessMerchantServiceChargeFee) =>
    unpricedFees.has(fee.name) ? null : fee.value;
  const setFeeType = (index: number, valueType: ValueType) => {
    markDirty("inPerson");
    setFees((prev) =>
      prev.map((f, i) => (i === index ? { ...f, valueType } : f))
    );
  };
  const setOnlineValue = (
    key: keyof OnlinePartnerBucketGroups,
    value: number | null
  ) => {
    markDirty("online");
    setOnlinePricing((prev) => ({
      ...prev,
      [key]: { ...prev[key], value: value ?? 0 },
    }));
  };
  const setOnlineType = (
    key: keyof OnlinePartnerBucketGroups,
    valueType: ValueType
  ) => {
    markDirty("online");
    setOnlinePricing((prev) => ({
      ...prev,
      [key]: { ...prev[key], valueType },
    }));
  };
  const setBucketRate = (field: string, next: Partial<OnlineRateDraft>) => {
    markDirty("online");
    setOnlineBucketDraft((prev) => ({
      ...prev,
      [field]: {
        value: null,
        valueType: onlineBucketValueType(field) ?? "Percentage",
        ...onlineBucketRates[field],
        ...prev[field],
        ...next,
      },
    }));
  };

  // Each section is gated on its own state only (PC-1316): an online rate out of
  // range, stored or edited, never holds back the in-person save or the mapping
  // it carries. The mapping itself gates both, since both write it.
  // Partner-pricing loading/unavailable is already named in the footer status.
  const OUT_OF_RANGE_REASON =
    "Fees outside the partner range are highlighted. Fix them first.";
  // A row seeded unpriced holds a placeholder 0, which would save as a real
  // 0 % rate (PC-1394).
  const inPersonUnpriced = fees.some((fee) => unpricedFees.has(fee.name));
  const inPersonDisabledReason = partnerPricingUnavailable
    ? undefined
    : inPersonUnpriced
      ? UNPRICED_IN_PERSON_REASON
      : partnerOnlineMappingBlocked
        ? mappingBlockedReason
        : inPersonOutOfRange
          ? OUT_OF_RANGE_REASON
          : undefined;
  const inPersonSaveDisabled =
    partnerPricingUnavailable ||
    inPersonUnpriced ||
    partnerOnlineMappingBlocked ||
    inPersonOutOfRange;
  // Either save maps the merchant, so both name it until a partner is stored.
  const savesNewMapping =
    hasPartner && !storedPartnerId(currentBusiness?.partnerId);
  // What an online save would carry, if anything: these states leave the stored
  // online pricing untouched, so there is nothing for the button to write.
  const onlineNothingToSaveReason = isUneditableOnline
    ? "This online pricing cannot be edited here."
    : onlineDisabled
      ? "Online pricing is locked for this merchant."
      : partnerOnlineUnlinked
        ? `Online pricing is not linked to a partner range, so there is nothing to save here.${
            savesNewMapping ? " Save the mapping on the In-person tab." : ""
          }`
        : !onlineConfigured
          ? "No online pricing to save yet."
          : undefined;
  // The in-person fees never hold an online save back, even one that maps the
  // merchant: the save does not carry them, and the backend defers their band
  // to the next in-person save (inPersonPricingRangePending).
  const onlineDisabledReason = partnerPricingUnavailable
    ? undefined
    : partnerOnlineMappingBlocked
      ? mappingBlockedReason
      : (onlineNothingToSaveReason ??
        (onlineOutOfRange ? OUT_OF_RANGE_REASON : undefined));
  const onlineSaveDisabled =
    partnerPricingUnavailable ||
    partnerOnlineMappingBlocked ||
    !!onlineNothingToSaveReason ||
    onlineOutOfRange;

  const inPersonSaveLabel = savesNewMapping
    ? "Confirm mapping & save in-person"
    : "Save in-person pricing";
  const onlineSaveLabel = savesNewMapping
    ? "Confirm mapping & save online"
    : "Save online pricing";

  const saveMapping = (): PricingSaveMapping => ({
    // A stored partner cannot be swapped on this save; remap is unmap then map.
    partnerId: storedPartnerId(currentBusiness?.partnerId) ?? partnerId ?? null,
    partnerPurchPriceProfileId: profileId ?? null,
    partnerOnlinePurchPriceProfileId:
      partnerOnlineUnlinked ||
      (onlineProfiles !== undefined && onlineProfiles.length === 0)
        ? null
        : (partnerOnlinePurchPriceProfileId ?? null),
  });

  const submitPricing = async (
    section: SaveSection,
    payload: UpdateBusinessPricingPayload
  ) => {
    if (!currentBusiness?._id) return;
    setSaving(section);
    setMappingSaveError(null);
    if (section === "inPerson") setInPersonSaveError(null);
    else setOnlineSaveError(null);
    try {
      await updateBusinessPricingServer(currentBusiness._id, payload);
      message.success(
        section === "inPerson"
          ? "In-person pricing saved"
          : "Online pricing saved"
      );
      // Merchant details omit pricing, so reseed from the payload just saved.
      // The other section keeps its draft.
      dirtyRef.current[section] = false;
      dirtyRef.current.mapping = false;
      dispatch(
        applySavedBusinessPricing({
          businessId: currentBusiness._id,
          pricing: payload,
        })
      );
    } catch (err) {
      const { message: detail, code } = pricingErrorBody(err);
      // PUT /pricing rethrows some Adyen failures without a code; never echo them.
      const adyenSyncFailed =
        isAdyenOnlinePricingSyncError(code) ||
        (!code && section === "online" && submittedToAdyen);
      const shown = adyenSyncFailed
        ? getOnlinePricingSyncErrorMessage(code)
        : detail || code || "Failed to save pricing";
      // A refused save was never written, so the operator is being told to fix
      // the form: keep the backend's own wording beside the section it is
      // about, instead of a toast that disappears.
      const errorSection = adyenSyncFailed
        ? "online"
        : pricingSaveErrorSection(code, section);
      if (errorSection === "online") {
        setActiveTab("online");
        setOnlineSaveError({
          message: shown,
          buckets:
            isOnlinePricingPayloadError(code) && detail
              ? resolveInvalidOnlinePricingBuckets(
                  { message: detail },
                  carriedBucketFields(onlineGroups)
                )
              : [],
        });
      } else if (errorSection === "inPerson") {
        setActiveTab("inperson");
        setInPersonSaveError(shown);
      } else {
        setMappingSaveError(shown);
      }
      message.error(shown);
    } finally {
      setSaving(undefined);
    }
  };

  const handleSaveInPerson = async () => {
    if (!currentBusiness) return;
    if (partnerPricingUnavailable) {
      message.error("Partner pricing is unavailable. Try again.");
      return;
    }
    if (inPersonDisabledReason) {
      message.error(inPersonDisabledReason);
      return;
    }
    await submitPricing(
      "inPerson",
      buildInPersonPricingSave({
        // Checked before the reconcile: it re-adds a hidden stored Amex, which
        // would turn an empty card into a one-fee list that erases the rest.
        fees: fees.length
          ? reconcileInPersonFeesForSave(
              fees,
              currentBusiness.merchantServiceChargeFees,
              hideInPersonAmex
            )
          : [],
        storedPartnerId: storedPartnerId(currentBusiness.partnerId),
        mapping: saveMapping(),
      })
    );
  };

  const handleSaveOnline = async () => {
    if (!currentBusiness) return;
    if (partnerPricingUnavailable) {
      message.error("Partner pricing is unavailable. Try again.");
      return;
    }
    if (onlineDisabledReason) {
      message.error(onlineDisabledReason);
      return;
    }
    const fourBucketMissingRates = ONLINE_BUCKETS.filter(
      ({ key }) => !isPricedOnlineValue(onlinePricing[key]?.value)
    );
    if (
      !isSixteenBucket &&
      fourBucketHasRates &&
      !fourBucketComplete &&
      !onlineLocked
    ) {
      message.error(
        `Set an online rate for: ${fourBucketMissingRates
          .map((bucket) => bucket.label)
          .join(", ")}.`
      );
      return;
    }
    if (onlineConfigured && bucketsWithoutARate.length > 0) {
      message.error(
        `Set an online rate for: ${bucketsWithoutARate
          .map((bucket) => bucket.label)
          .join(", ")}.`
      );
      return;
    }
    // A 16-bucket payload carries exactly the groups its template pair prices;
    // a four-bucket one exactly ONLINE_BUCKETS' key set (template ∪
    // legacy-with-value) — see filterOnlinePricingToKeys. onlinePricing is
    // already wallet-seeded at load via seedOnline (PC-578).
    const onlinePricingToSave: BusinessOnlinePricing = isSixteenBucket
      ? buildSixteenBucketOnlinePricing(
          onlineGroups,
          groupDraftToSave,
          currentBusiness.onlinePricing
        )
      : filterOnlinePricingToKeys(
          onlinePricing,
          ONLINE_BUCKETS.map((b) => b.key)
        );
    // The groups object alone would have the write path fan each group's value
    // out per bucket, flattening a bucket priced away from its group. The rows
    // keep that divergence, on a partner-mapped merchant as much as a direct one.
    const onlineBucketFeesToSave = bucketsEditable
      ? buildOnlineMerchantServiceChargeFees(onlineGroups, onlineBucketRates)
      : undefined;
    await submitPricing(
      "online",
      buildOnlinePricingSave({
        mapping: saveMapping(),
        onlinePricing: onlinePricingToSave,
        onlineMerchantServiceChargeFees: onlineBucketFeesToSave,
      })
    );
  };

  // Each pane speaks for its own range: a partner can price in person and
  // nothing online, so one banner's verdict is not the other's.
  const banner = hasPartner ? (
    <PartnerOverlayBanner
      partnerName={selectedPartner?.name}
      scope="in-person"
      rangeState={inPersonRangeState}
    />
  ) : (
    <NoPartnerBanner />
  );
  const onlineBanner = hasPartner ? (
    <>
      <PartnerOverlayBanner
        partnerName={selectedPartner?.name}
        scope="online"
        rangeState={
          partnerOnlineMappingBlocked
            ? "blocked"
            : partnerOnlineUnlinked
              ? "unlinked"
              : onlineRangeState
        }
        reason={
          partnerOnlineUnlinked ? onlineUnlinkedReason : mappingBlockedReason
        }
      />
      {/* An in-person save remapped the merchant and left online for later;
          the backend holds the next online save to the new range in full. */}
      {currentBusiness?.onlinePricingRangePending && !partnerOnlineUnlinked && (
        <div
          data-testid="online-range-pending"
          style={{
            background: "#FFF8E6",
            border: "1px solid #FFE7A3",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          Online pricing has not been saved against the current online range
          yet. The next online save is checked against that range in full.
        </div>
      )}
    </>
  ) : (
    <NoPartnerBanner />
  );

  const inPersonTab = (
    <div>
      {banner}
      {/* An online save mapped the merchant and left in-person for later; the
          backend holds the next in-person save to the new range in full. */}
      {currentBusiness?.inPersonPricingRangePending && hasPartner && (
        <div
          data-testid="in-person-range-pending"
          style={{
            background: "#FFF8E6",
            border: "1px solid #FFE7A3",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          In-person fees have not been saved against the current in-person range
          yet. The next in-person save is checked against that range in full.
        </div>
      )}
      {inPersonCannotGroup && (
        <div
          style={{
            background: "#F5F2EC",
            border: "1px solid #E8E2D8",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 13,
            color: "rgba(0,0,0,0.55)",
          }}
        >
          {CANNOT_GROUP_PRICING_NOTE}
        </div>
      )}
      {!hasPartner ? (
        <>
          <Card
            title="In-person buckets"
            meta={`${fees.length} buckets · Klearly direct fees`}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                columnGap: 24,
                rowGap: 18,
              }}
            >
              {feeRenderOrder.map((index) => {
                const fee = fees[index];
                return (
                  <div
                    key={`${fee.name}-${index}`}
                    data-testid={`in-person-fee-${fee.name}`}
                  >
                    <FeeInputRow
                      label={displayFeeLabel(fee.name)}
                      value={feeDisplayValue(fee)}
                      valueType={fee.valueType}
                      onValue={(v) => setFeeValue(index, v)}
                      onType={(t) => setFeeType(index, t)}
                    />
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      ) : (
        rangeGroups.map((g) => (
          <RangeGroupSection
            key={g.key}
            label={g.label}
            floor={g.floor}
            max={g.max}
            valueType={g.valueType}
          >
            {g.fees.map(({ index, fee, comparable }, i) => (
              <FeeInputRow
                key={`${fee.name}-${index}`}
                shareColumns
                rowStyle={{
                  padding: "14px 20px",
                  borderTop: i > 0 ? "1px solid #F1EEE8" : "none",
                  background: "#FEFCF8",
                }}
                label={displayFeeLabel(fee.name)}
                value={feeDisplayValue(fee)}
                valueType={fee.valueType}
                onValue={(v) => setFeeValue(index, v)}
                onType={(t) => setFeeType(index, t)}
                status={
                  comparable
                    ? feeStatus(fee.value, g.valueType, g.floor, g.max)
                    : null
                }
                note={comparable ? undefined : "Different unit to the range"}
              />
            ))}
          </RangeGroupSection>
        ))
      )}
      {ungrouped.length > 0 && (
        <Card
          title="Other fees"
          meta={
            inPersonCannotGroup
              ? CANNOT_GROUP_PRICING_NOTE
              : "No partner range — not validated"
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {ungrouped.map(({ index, fee }) => (
              <FeeInputRow
                key={`${fee.name}-${index}`}
                label={displayFeeLabel(fee.name)}
                value={feeDisplayValue(fee)}
                valueType={fee.valueType}
                onValue={(v) => setFeeValue(index, v)}
                onType={(t) => setFeeType(index, t)}
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  const sixteenBucketOnlineTab = (
    <div>
      {onlineBanner}
      {onlinePreview.loading || waitingForMerchantCoverage ? (
        <Card
          title={showOnlineGroups ? "Online groups" : "Online buckets"}
          meta={
            coverageFromMerchant || !hasPartner
              ? "Loading buckets…"
              : "Loading partner ranges…"
          }
        >
          <div style={{ opacity: 0.6 }}>
            {coverageFromMerchant || !hasPartner
              ? "Waiting for the merchant's online pricing buckets."
              : "Waiting for the partner's online pricing ranges."}
          </div>
        </Card>
      ) : (
        <>
          {showOnlineGroups && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 12,
                gap: 12,
              }}
            >
              <Typography.Text strong style={{ fontSize: 15 }}>
                {onlineCardSplitHeading(
                  // The heading names the split the groups below it are drawn
                  // from, so it reads the selected range only where that range's
                  // coverage is what drew them — `useSelectedProfileBands` is
                  // that same test. A profile serving a template but no coverage
                  // would otherwise rename groups the preview still owns.
                  (useSelectedProfileBands
                    ? selectedOnlineProfile?.onlineCardSplitTemplate
                    : undefined) ??
                    onlinePreview.preview?.pricing?.onlineCardSplitTemplate ??
                    currentBusiness?.onlineCardSplitTemplate
                )}
              </Typography.Text>
              <span style={{ color: "rgba(0,0,0,0.4)", fontSize: 12 }}>
                {onlineGroups.length} groups ·{" "}
                {onlineGroups.reduce((n, g) => n + g.buckets.length, 0)} buckets
              </span>
            </div>
          )}
          {hasPartner &&
            !partnerHasNoOnlineRange &&
            !partnerRangeMissingLocalMethod &&
            groupsWithoutABand.length > 0 && (
              <div
                style={{
                  background: "#FFF8E6",
                  border: "1px solid #FFE7A3",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 12,
                  fontSize: 13,
                }}
              >
                No partner range loaded for {groupsWithoutABand.join(", ")}.
                Those rates are not checked here — the backend will still reject
                a rate outside the partner's band.
              </div>
            )}
          {onlineRangesUnavailable ? (
            <Card title="Online buckets" meta="Ranges not loaded">
              <div
                data-testid="online-preview-unavailable"
                role="alert"
                style={{ fontSize: 13 }}
              >
                The online ranges could not be loaded, so the buckets to price
                cannot be listed. Reopen the merchant to try again.
                {onlinePreview.failureReason && (
                  <div
                    data-testid="online-preview-failure-reason"
                    style={{ marginTop: 6, color: "rgba(0,0,0,0.45)" }}
                  >
                    {onlinePreview.failureReason}
                  </div>
                )}
              </div>
            </Card>
          ) : !showOnlineGroups ? (
            <Card
              title="Online buckets"
              meta={`${onlineBuckets.length} buckets · Klearly direct fees`}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  columnGap: 24,
                  rowGap: 18,
                }}
              >
                {onlineBuckets.map(({ field, label }) => {
                  const rate = onlineBucketRates[field];
                  return (
                    <div
                      key={field}
                      data-testid={`online-bucket-${field}`}
                      style={{
                        background: onlineSaveError?.buckets?.includes(field)
                          ? "#FDECEC"
                          : undefined,
                      }}
                    >
                      <FeeInputRow
                        label={label}
                        value={rate?.value ?? null}
                        valueType={
                          rate?.valueType ??
                          onlineBucketValueType(field) ??
                          "Percentage"
                        }
                        disabled={onlineLocked}
                        onValue={(value) => setBucketRate(field, { value })}
                        onType={(valueType) =>
                          setBucketRate(field, { valueType })
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          ) : (
            onlineGroupRows.map(({ group, band }) => (
              <OnlineGroupSection
                key={group.key}
                group={group}
                band={band}
                disabled={onlineLocked}
                bucketRows={onlineBucketRowByField}
                spread={onlineGroupBucketSpread(group, onlineBucketRates)}
                bucketsEditable={bucketsEditable}
                invalidBuckets={onlineSaveError?.buckets}
                onBucketValue={(field, value) =>
                  setBucketRate(field, { value })
                }
                onBucketType={(field, valueType) =>
                  setBucketRate(field, { valueType })
                }
              />
            ))
          )}
        </>
      )}
    </div>
  );

  const fourBucketOnlineTab = (
    <div>
      {onlineBanner}
      {isUneditableOnline ? (
        <Card title="Online buckets" meta="Not editable">
          <div style={{ opacity: 0.6 }}>
            This merchant is priced on an online structure this version of KBM
            cannot edit without dropping part of it. Its rates are shown in
            Business Details and won't be changed on save.
          </div>
        </Card>
      ) : !fourBucketHasRates ? (
        <Card title="Online buckets" meta="Not configured">
          <div style={{ opacity: 0.6 }}>
            This merchant has no online pricing configured. Online fees aren't
            edited here and won't be changed on save.
          </div>
        </Card>
      ) : (
        <Card
          title="Online buckets"
          meta={`${ONLINE_BUCKETS.length} buckets · ${onlineFourBucketMeta}`}
        >
          <div
            style={pricingBandParentStyle(
              { rowGap: 14 },
              PRICING_OVERLAY_COLUMNS
            )}
          >
            {ONLINE_BUCKETS.map(({ key, label, valueType }) => {
              const band = hasPartner
                ? configuredOnlineBand(bandFor(key))
                : undefined;
              const value = onlinePricing[key]?.value ?? 0;
              // Merchant-chosen fee type drives the input; the partner range keeps
              // its own type for the badge/status (mirrors in-person parity).
              const feeType = onlinePricing[key]?.valueType ?? valueType;
              let status: FeeStatus = null;
              if (band) {
                status = feeStatus(value, valueType, band.min, band.max);
              }
              return (
                <FeeInputRow
                  key={key}
                  shareColumns
                  splitMeta
                  label={label}
                  value={value}
                  valueType={feeType}
                  disabled={onlineLocked}
                  onValue={(v) => setOnlineValue(key, v)}
                  onType={(t) => setOnlineType(key, t)}
                  status={status}
                  badge={
                    band ? (
                      <RangeBadge
                        floor={band.min}
                        max={band.max}
                        valueType={valueType}
                        compact
                      />
                    ) : undefined
                  }
                />
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );

  const mappingStatus = (
    <span style={{ color: "rgba(0,0,0,0.45)", fontSize: 13 }}>
      <span
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: 4,
          background: hasPartner ? ACCENT : "#bbb",
          marginRight: 8,
        }}
      />
      {partnerPricingLoading
        ? "Loading partner pricing…"
        : partnerPricingUnavailable
          ? "Partner pricing unavailable"
          : hasPartner
            ? `Mapped to ${selectedPartner?.name ?? "partner"}`
            : "No partner mapped yet."}
    </span>
  );

  const inPersonSection = (
    <div>
      {inPersonTab}
      <SectionSaveFooter
        section="in-person"
        reason={inPersonDisabledReason}
        error={inPersonSaveError ?? undefined}
      >
        <Button
          type="primary"
          htmlType="button"
          loading={saving === "inPerson"}
          disabled={inPersonSaveDisabled || saving === "online"}
          onClick={handleSaveInPerson}
        >
          {inPersonSaveLabel}
        </Button>
      </SectionSaveFooter>
    </div>
  );

  const onlineSection = (
    <div>
      {isSixteenBucket ? sixteenBucketOnlineTab : fourBucketOnlineTab}
      <SectionSaveFooter
        section="online"
        reason={onlineDisabledReason}
        error={onlineSaveError?.message}
      >
        <Button
          type="primary"
          htmlType="button"
          loading={saving === "online"}
          disabled={onlineSaveDisabled || saving === "inPerson"}
          onClick={handleSaveOnline}
        >
          {onlineSaveLabel}
        </Button>
      </SectionSaveFooter>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Partner + profile selectors */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 280 }}>
          <FieldLabel>PARTNER</FieldLabel>
          <PartnerSelector
            value={partnerId}
            disabled={partnerRemapLocked}
            disabledHint={partnerRemapLocked ? PARTNER_REMAP_HINT : undefined}
            allowClear={!partnerRemapLocked}
            onChange={(id?: string) => {
              if (partnerRemapLocked) return;
              markDirty("inPerson", "online", "mapping");
              pendingOnlineMappingRef.current = id;
              pendingInPersonMappingRef.current = id;
              setPartnerId(id || undefined);
              setProfileId(undefined);
              setPartnerOnlinePurchPriceProfileId(undefined);
              setSelectedPartner(undefined);
              setProfiles(undefined);
              setOnlineProfiles(undefined);
              setSelectedProfile(undefined);
              setSelectedOnlineProfile(undefined);
            }}
          />
        </div>
        <div style={{ width: 220 }}>
          <FieldLabel>
            {activeTab === "online"
              ? "ONLINE PRICING PROFILE"
              : "PRICING PROFILE"}
          </FieldLabel>
          {/* A range of the mapped partner, not the partner: only the partner
              selector is remap-locked, and Adyen submission locks neither. */}
          <Select
            data-testid="pricing-profile-select"
            style={{ width: "100%" }}
            value={
              !partnerId
                ? undefined
                : activeTab === "online"
                  ? !partnerOnlineUnlinked &&
                    onlineProfiles?.some(
                      (profile) =>
                        profile._id === partnerOnlinePurchPriceProfileId
                    )
                    ? partnerOnlinePurchPriceProfileId
                    : undefined
                  : profileId
            }
            placeholder={
              activeTab !== "online"
                ? "Default"
                : partnerOnlineUnlinked
                  ? "Not linked"
                  : !onlineProfiles?.length
                    ? "Not configured"
                    : "Default"
            }
            disabled={
              !partnerId ||
              (activeTab === "online"
                ? partnerOnlineUnlinked || !onlineProfiles?.length
                : !profiles?.length)
            }
            onChange={(id) => {
              if (activeTab === "online") {
                if (partnerOnlineUnlinked) return;
                markDirty("online");
                const profile = onlineProfiles?.find(
                  (candidate) => candidate._id === id
                );
                setPartnerOnlinePurchPriceProfileId(id);
                setSelectedOnlineProfile(profile);
                if (!isSixteenBucketOnlineProfile(profile)) {
                  const { pricing, seeded } = fillUnpricedOnlinePricing(
                    onlinePricing,
                    onlinePricingForProfile(
                      profile,
                      selectedPartner?.defaultOnlinePricing
                    ),
                    onlineRangeSeedRef.current
                  );
                  onlineRangeSeedRef.current = seeded;
                  setOnlinePricing(pricing);
                }
                return;
              }
              markDirty("inPerson");
              setProfileId(id);
              setSelectedProfile(
                profiles?.find((profile) => profile._id === id)
              );
            }}
            options={(activeTab === "online" ? onlineProfiles : profiles)?.map(
              (profile) => ({
                label: profile.name,
                value: profile._id,
                key: profile._id,
              })
            )}
          />
        </div>
      </div>
      {/* The mapping is written by either tab's save, so its state sits with
          the selectors rather than in one tab's footer. */}
      <div style={{ marginTop: -8 }}>
        {mappingStatus}
        {mappingSaveError && (
          <div
            data-testid="pricing-mapping-save-error"
            role="alert"
            style={{ color: "#A8231A", fontSize: 13, marginTop: 4 }}
          >
            {mappingSaveError}
          </div>
        )}
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "inperson",
            label: <TabLabel text="In-person" warn={inPersonOutOfRange} />,
            children: inPersonSection,
          },
          {
            key: "online",
            label: (
              <TabLabel
                text="Online pricing"
                warn={
                  onlineOutOfRange ||
                  partnerOnlineMappingBlocked ||
                  partnerOnlineUnlinked
                }
              />
            ),
            children: onlineSection,
          },
        ]}
      />
    </div>
  );
}

// One save per tab (PC-1316): the reason a button is disabled, and the
// backend's refusal of its last save, sit beside that button.
function SectionSaveFooter({
  section,
  reason,
  error,
  children,
}: {
  section: "in-person" | "online";
  reason?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid={`${section}-save-footer`}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: 12,
        borderTop: "1px solid #f0f0f0",
        paddingTop: 16,
      }}
    >
      {error && (
        <span
          data-testid={`${section}-pricing-save-error`}
          role="alert"
          style={{ color: "#A8231A", fontSize: 13 }}
        >
          {error}
        </span>
      )}
      {reason && (
        <span
          data-testid={`${section}-save-disabled-reason`}
          style={{ color: "#d46b08", fontSize: 13 }}
        >
          {reason}
        </span>
      )}
      {children}
    </div>
  );
}

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div
      style={{
        fontSize: 11,
        letterSpacing: 0.6,
        color: "rgba(0,0,0,0.45)",
        marginBottom: 6,
      }}
    >
      {children}
      {required && <span style={{ color: "#ff4d4f" }}> *</span>}
    </div>
  );
}

function TabLabel({ text, warn }: { text: string; warn: boolean }) {
  return (
    <span>
      {text}
      {warn && <WarningFilled style={{ color: "#faad14", marginLeft: 6 }} />}
    </span>
  );
}

function Card({
  title,
  meta,
  badge,
  children,
}: {
  title: string;
  meta?: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          gap: 12,
        }}
      >
        <Typography.Text strong style={{ fontSize: 15 }}>
          {title}
        </Typography.Text>
        {badge}
        {meta && (
          <span style={{ color: "rgba(0,0,0,0.4)", fontSize: 12 }}>{meta}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function NoPartnerBanner() {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        background: "#F1EEFE",
        border: "1px solid #E3DCFB",
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: ACCENT,
          color: "#fff",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        K
      </div>
      <div>
        <div style={{ fontWeight: 600 }}>
          No partner mapped — the merchant pays Klearly directly.
        </div>
        <div style={{ color: "rgba(0,0,0,0.55)", fontSize: 13 }}>
          There is no partner markup on these fees. Pick a partner above to load
          a profile range and add markup on top.
        </div>
      </div>
    </div>
  );
}

/** What this pane knows about the partner's range, which is not the same as
 *  what it can draw. `none` is a fact about the partner and reads as one, and
 *  `blocked` and `unlinked` state what no range prices (`reason`);
 *  `loading` and `unknown` leave the rows equally unchecked but claim nothing
 *  about what the partner prices, because saying "no range configured" off an unanswered
 *  request reads the merchant's own stored floor back as the partner's live
 *  range just as surely as inventing a band would. */
type PartnerRangeState =
  "overlaid" | "none" | "loading" | "unknown" | "blocked" | "unlinked";

function PartnerOverlayBanner({
  partnerName,
  scope,
  rangeState = "overlaid",
  reason,
}: {
  partnerName?: string;
  scope: "online" | "in-person";
  rangeState?: PartnerRangeState;
  reason?: string;
}) {
  const who = partnerName ?? "The partner";
  const whose = partnerName ? `${partnerName}'s` : "The partner's";
  const copy: Record<PartnerRangeState, { title: string; detail: string }> = {
    overlaid: {
      title: `${whose} ${scope} price range is overlaid on these fees.`,
      detail:
        "You can edit the merchant's fees but not the partner's range (configured on the partner side). Fees outside the partner's range are highlighted — fix them before saving.",
    },
    none: {
      title: `${who} has no ${scope} price range configured.`,
      detail:
        "The merchant's fees stay editable, but nothing here is checked against a partner range. It is configured on the partner side.",
    },
    loading: {
      title: `Loading ${whose} ${scope} price range.`,
      detail:
        "Nothing below is checked against a partner range until the range arrives.",
    },
    blocked: {
      title: reason ?? `${whose} ${scope} price range cannot be mapped.`,
      detail:
        "Saving stays disabled on both tabs. The merchant's fees stay editable.",
    },
    unlinked: {
      title: reason ?? `${who} has no ${scope} price range for this merchant.`,
      detail:
        "Saving links the in-person range only. Online stays unlinked from the partner and keeps the merchant's stored rates, which cannot be edited here.",
    },
    unknown: {
      title: `${whose} ${scope} price range is not known here.`,
      detail:
        "Nothing below is checked against a partner range, and none of this says the partner has none — reopen the merchant to try again. The backend still rejects a rate outside the partner's band.",
    },
  };
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        background: "#FFF8E6",
        border: "1px solid #FFE7A3",
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "#F5A623",
          color: "#fff",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        !
      </div>
      <div>
        <div style={{ fontWeight: 600 }}>{copy[rangeState].title}</div>
        <div style={{ color: "rgba(0,0,0,0.55)", fontSize: 13 }}>
          {copy[rangeState].detail}
        </div>
      </div>
    </div>
  );
}

// Shared group header: title left, range in the middle column, trailing right.
// Subgrid of the group parent so the badge cannot drift with the label.
function GroupSectionHeader({
  label,
  children,
  trailing,
}: {
  label: string;
  children?: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div
      data-testid="group-section-header"
      style={pricingBandSubgridStyle({
        background: "#F6F5FB",
        padding: "14px 20px",
      })}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minWidth: 0,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 3,
            background: ACCENT,
            flexShrink: 0,
          }}
        />
        <Typography.Text strong style={{ fontSize: 15 }}>
          {label}
        </Typography.Text>
      </div>
      <div style={{ justifySelf: "start", minWidth: 0 }}>
        {children ?? <span />}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifySelf: "end",
          gap: 6,
        }}
      >
        {trailing}
      </div>
    </div>
  );
}

// Partner-mapped in-person group: titled section with member merchant buckets
// underneath, so the bucket→group mapping is visible rather than a flat list.
function RangeGroupSection({
  label,
  floor,
  max,
  valueType,
  children,
}: {
  label: string;
  floor: number;
  max: number;
  valueType: ValueType;
  children: React.ReactNode;
}) {
  return (
    <div
      style={pricingBandParentStyle({
        border: "1px solid #ECEAF3",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      })}
    >
      <GroupSectionHeader label={label}>
        <PartnerRangeHeader floor={floor} max={max} valueType={valueType} />
      </GroupSectionHeader>
      {children}
    </div>
  );
}

// A bucket beside the partner band governing it: the band of the group the
// bucket sits under. `blocking` is the narrower question the save is gated on —
// see the row build in PricingNewTab.
type OnlineBucketBandRow = {
  field: string;
  label: string;
  band?: Band;
  rate: OnlineBucketRates[string];
  status: FeeStatus;
  comparable: boolean;
  unitMismatch: boolean;
  missingPartnerRange: boolean;
  blocking: boolean;
};

// A priced group of the 16-bucket structure: the partner band on the header, the
// buckets it covers listed underneath, each priced and checked on its own.
//
// The group carries no rate input. It is a band, not a price: the merchant's
// price lives on the buckets, and one rate on the header could not express two
// buckets of the group differing.
function OnlineGroupSection({
  group,
  band,
  disabled,
  bucketRows,
  spread,
  bucketsEditable,
  invalidBuckets,
  onBucketValue,
  onBucketType,
}: {
  group: OnlinePricingGroup;
  band?: Band;
  disabled?: boolean;
  bucketRows: Record<string, OnlineBucketBandRow>;
  spread?: OnlineGroupBucketSpread;
  bucketsEditable?: boolean;
  invalidBuckets?: string[];
  onBucketValue: (field: string, value: number | null) => void;
  onBucketType: (field: string, valueType: ValueType) => void;
}) {
  const hasRange =
    !!band && typeof band.min === "number" && typeof band.max === "number";
  return (
    <div
      data-testid={`online-group-${group.key}`}
      style={pricingBandParentStyle({
        border: "1px solid #ECEAF3",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      })}
    >
      <GroupSectionHeader
        label={group.label}
        trailing={
          spread?.diverged ? <SpreadMarker spread={spread} /> : undefined
        }
      >
        {hasRange ? (
          <PartnerRangeHeader
            floor={band?.min as number}
            max={band?.max as number}
            valueType={band?.valueType as ValueType}
          />
        ) : undefined}
      </GroupSectionHeader>
      {group.buckets.map((bucket, index) => {
        const row = bucketRows[bucket.field];
        const bucketRate = row?.rate;
        // An emptied input still has to show the unit it will be read in.
        const bucketUnit =
          bucketRate?.valueType ??
          onlineBucketValueType(bucket.field) ??
          "Percentage";
        return (
          <div
            key={bucket.field}
            data-testid={`online-bucket-${bucket.field}`}
            style={pricingBandSubgridStyle({
              padding: "10px 20px",
              borderTop: index > 0 ? "1px solid #F1EEE8" : "none",
              background:
                invalidBuckets?.includes(bucket.field) || row?.blocking
                  ? "#FDECEC"
                  : "#FEFCF8",
            })}
          >
            <span
              style={{
                color: "rgba(0,0,0,0.65)",
                ...bucketLabelStyle,
              }}
            >
              {bucket.label}
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
                justifySelf: "start",
              }}
            >
              {row?.missingPartnerRange && (
                <span style={{ fontSize: 11, color: "#CF1322" }}>
                  {NO_PARTNER_RANGE_HINT}
                </span>
              )}
              {!row?.comparable && (
                <span
                  style={{
                    fontSize: 11,
                    color: row?.unitMismatch ? "#CF1322" : "#C77700",
                  }}
                >
                  {row?.unitMismatch ? UNIT_MISMATCH_HINT : NOT_COMPARABLE_HINT}
                </span>
              )}
              {row?.status && <StatusPill status={row.status} />}
            </div>
            {bucketsEditable ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  justifySelf: "end",
                }}
              >
                <InputNumber
                  controls={false}
                  min={0}
                  value={bucketRate?.value ?? null}
                  disabled={disabled}
                  style={{ width: 92 }}
                  aria-label={`${bucket.label} rate`}
                  addonBefore={bucketUnit === "Fixed" ? "€" : undefined}
                  addonAfter={bucketUnit === "Percentage" ? "%" : undefined}
                  onChange={(v) =>
                    onBucketValue(bucket.field, v as number | null)
                  }
                />
                <Segmented
                  size="small"
                  value={bucketUnit}
                  disabled={disabled}
                  onChange={(v) => onBucketType(bucket.field, v as ValueType)}
                  options={[
                    { label: "€", value: "Fixed" },
                    { label: "%", value: "Percentage" },
                  ]}
                />
              </div>
            ) : (
              <span style={{ fontWeight: 500, justifySelf: "end" }}>
                {bucketRate ? fmt(bucketRate.value, bucketRate.valueType) : "—"}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// A group whose buckets don't all carry one rate: the group value is then only
// the coarse control, not what the merchant is charged, so the spread is shown
// beside it. Mixed units have no range to show — €0.16 to 1.49 % is not one — so
// they read as "Custom".
function SpreadMarker({ spread }: { spread: OnlineGroupBucketSpread }) {
  return (
    <Pill bg="#F1EEFE" color={ACCENT}>
      {spread.valueType
        ? `${fmt(spread.min, spread.valueType)} – ${fmt(
            spread.max,
            spread.valueType
          )}`
        : "Custom"}
    </Pill>
  );
}

// The PARTNER RANGE caption above its badge, as a group header carries it. Both
// the in-person and the online header render this one, so the two panes cannot
// drift apart on how a range reads.
function PartnerRangeHeader({
  floor,
  max,
  valueType,
}: {
  floor: number;
  max: number;
  valueType: ValueType;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
      }}
    >
      <span
        style={{
          fontSize: 10,
          letterSpacing: 0.6,
          fontWeight: 600,
          color: ACCENT,
        }}
      >
        PARTNER RANGE
      </span>
      <RangeBadge floor={floor} max={max} valueType={valueType} />
    </div>
  );
}

// `compact` drops the FLOOR/MAX labels: it is for a badge sitting inside a fee
// row, where the row already names what it prices.
function RangeBadge({
  floor,
  max,
  valueType,
  compact,
}: {
  floor: number;
  max: number;
  valueType: ValueType;
  compact?: boolean;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        background: `linear-gradient(90deg, ${ACCENT}, #8B6FF5)`,
        color: "#fff",
        borderRadius: 10,
        padding: compact ? "4px 10px" : "8px 14px",
        fontSize: compact ? 12 : 13,
      }}
    >
      {!compact && (
        <span style={{ opacity: 0.8, fontSize: 10, letterSpacing: 0.5 }}>
          FLOOR
        </span>
      )}
      <b>{fmt(floor, valueType)}</b>
      <span style={{ opacity: 0.7 }}>—</span>
      {!compact && (
        <span style={{ opacity: 0.8, fontSize: 10, letterSpacing: 0.5 }}>
          MAX
        </span>
      )}
      <b>{fmt(max, valueType)}</b>
    </div>
  );
}

function StatusPill({ status }: { status: FeeStatus }) {
  if (!status) return null;
  if (status.kind === "markup") {
    return (
      <Pill bg="#E7F7EE" color="#1E9E5A">
        <CheckCircleFilled style={{ marginRight: 4 }} />
        {status.text}
      </Pill>
    );
  }
  const isAbove = status.kind === "above";
  const text = isAbove ? "↑ Above max" : "↓ Below floor";
  return (
    <Pill
      bg={isAbove ? "#FDECEC" : "#FEF3E2"}
      color={isAbove ? "#D4380D" : "#C77700"}
    >
      {text}
    </Pill>
  );
}

function Pill({
  bg,
  color,
  children,
}: {
  bg: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <span
      style={{
        background: bg,
        color,
        borderRadius: 20,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function FeeInputRow({
  label,
  value,
  valueType,
  disabled,
  status,
  badge,
  note,
  shareColumns,
  splitMeta,
  rowStyle,
  onValue,
  onType,
}: {
  label: string;
  // `null` is an unpriced bucket, which has to render empty: a 0 shown here
  // would read as free and save as a real rate.
  value: number | null;
  valueType: ValueType;
  disabled?: boolean;
  status?: FeeStatus;
  badge?: React.ReactNode;
  note?: string;
  shareColumns?: boolean;
  splitMeta?: boolean;
  rowStyle?: CSSProperties;
  onValue: (v: number | null) => void;
  onType: (t: ValueType) => void;
}) {
  return (
    <div
      data-testid="fee-input-row"
      style={
        shareColumns
          ? pricingBandSubgridStyle({ minWidth: 0, ...rowStyle })
          : pricingBandRowStyle({
              minWidth: 0,
              ...(splitMeta
                ? { gridTemplateColumns: PRICING_OVERLAY_COLUMNS }
                : {}),
              ...rowStyle,
            })
      }
    >
      <span style={bucketLabelStyle}>{label}</span>
      {splitMeta && (
        <div style={{ justifySelf: "start", minWidth: 0 }}>{badge}</div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          minWidth: 0,
          justifySelf: "start",
        }}
      >
        {!splitMeta && badge}
        {note && (
          <span style={{ color: "rgba(0,0,0,0.45)", fontSize: 12 }}>
            {note}
          </span>
        )}
        {status && <StatusPill status={status} />}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          justifySelf: "end",
        }}
      >
        <InputNumber
          controls={false}
          min={0}
          value={value}
          disabled={disabled}
          style={{ width: 92 }}
          aria-label={`${label} rate`}
          addonBefore={valueType === "Fixed" ? "€" : undefined}
          addonAfter={valueType === "Percentage" ? "%" : undefined}
          onChange={(v) => onValue(v as number | null)}
        />
        <Segmented
          size="small"
          value={valueType}
          disabled={disabled}
          onChange={(v) => onType(v as ValueType)}
          options={[
            { label: "€", value: "Fixed" },
            { label: "%", value: "Percentage" },
          ]}
        />
      </div>
    </div>
  );
}
